import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import LoadingButton from '../../../../../components/buttons/Button';
import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';

import {
  SaveInternationalServiceFeeLadderBIZcore,
  SaveInternationalUSDtoUSDServiceFeeLadderBIZcore
} from '../../../../../redux/actions/business/core/feeLaddersActions';

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';
const UNBOUNDED = -1;

// Mirrors the read-only analysis on the page so the editor flags exactly what
// the visualiser would, before anything is written.
export const analyseBands = (bands) => {
  const issues = [];
  const ordered = [...bands].sort((a, b) => Number(a.min || 0) - Number(b.min || 0));

  ordered.forEach((b, i) => {
    const lo = Number(b.min || 0);
    const hi = b.unbounded ? UNBOUNDED : Number(b.max || 0);
    if (lo < 0) issues.push({ level: 'error', text: `Band ${i + 1}: minimum cannot be negative` });
    if (!b.unbounded && hi <= lo) {
      issues.push({ level: 'error', text: `Band ${i + 1}: maximum must be greater than the minimum` });
    }
    if (b.unbounded && i !== ordered.length - 1) {
      issues.push({ level: 'error', text: `Band ${i + 1} is unbounded but is not the highest band` });
    }
  });

  for (let i = 1; i < ordered.length; i++) {
    const prev = ordered[i - 1];
    if (prev.unbounded) continue;
    const prevMax = Number(prev.max || 0);
    const curMin = Number(ordered[i].min || 0);
    if (curMin < prevMax) {
      issues.push({ level: 'error', text: `Bands ${i} and ${i + 1} overlap between ${curMin} and ${prevMax}` });
    } else if (curMin > prevMax) {
      issues.push({ level: 'warn', text: `Gap between bands ${i} and ${i + 1}: ${prevMax} to ${curMin} matches no band` });
    }
  }

  if (ordered.length && Number(ordered[0].min || 0) > 0) {
    issues.push({ level: 'warn', text: `Amounts below ${ordered[0].min} match no band` });
  }
  if (ordered.length && !ordered[ordered.length - 1].unbounded) {
    issues.push({ level: 'warn', text: 'Amounts above the highest band match no band' });
  }
  return issues;
};

const LadderEditorModal = ({
  open,
  setOpen,
  lane,
  tenantId,
  handleGet
}) => {
  const dispatch = useDispatch();
  const { handleEditAmount, handleCalculateAmount } = useCurrencyService();
  const [loading, setLoading] = useState(false);
  const [bands, setBands] = useState([]);

  const currency = lane?.bands?.[0]?.fee_currency || "USD";

  // Amounts are stored as minor units; the editor works in major units and
  // converts back on save, so what you type is what the rest of the app shows.
  const toMajor = (v) => handleEditAmount(currency, v || 0);
  const toMinor = (v) => handleCalculateAmount(currency, v || 0);

  useEffect(() => {
    if (!open || !lane) return;
    setBands(lane.bands.map((b) => ({
      id: b.id,
      min: toMajor(b.min_trx_amount),
      max: Number(b.max_trx_amount) === UNBOUNDED ? "" : toMajor(b.max_trx_amount),
      unbounded: Number(b.max_trx_amount) === UNBOUNDED,
      markup_percent: String(b.markup_percent ?? 0),
      fee_amount_regular: toMajor(b.fee_amount_regular),
      fee_amount_priority: toMajor(b.fee_amount_priority),
      is_active: b.is_active !== false,
    })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lane]);

  const handleClose = () => {
    setOpen(false);
    setBands([]);
  };

  const patch = (index, field, value) => {
    setBands((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)));
  };

  // Halving a band is the natural way to insert in the MIDDLE without leaving a
  // gap: the new band takes the upper half of the one it came from.
  const splitBand = (index) => {
    setBands((prev) => {
      const band = prev[index];
      const lo = Number(String(band.min).replace(/[^\d.]/g, "") || 0);
      const hi = band.unbounded
        ? lo * 2 + 1000
        : Number(String(band.max).replace(/[^\d.]/g, "") || 0);
      const mid = Math.round((lo + hi) / 2);
      if (!band.unbounded && (mid <= lo || mid >= hi)) {
        toast.error("This band is too narrow to split");
        return prev;
      }
      const upper = {
        ...band,
        id: undefined,          // a genuinely new row
        min: String(mid),
        max: band.unbounded ? "" : String(hi),
        unbounded: band.unbounded,
      };
      const lower = { ...band, max: String(mid), unbounded: false };
      const next = [...prev];
      next.splice(index, 1, lower, upper);
      return next;
    });
  };

  const removeBand = (index) => {
    setBands((prev) => prev.filter((_, i) => i !== index));
  };

  const addAtStart = () => {
    setBands((prev) => {
      const firstMin = Number(String(prev[0]?.min || 0).replace(/[^\d.]/g, "") || 0);
      return [{
        id: undefined, min: "0", max: String(firstMin), unbounded: false,
        markup_percent: "0", fee_amount_regular: "0", fee_amount_priority: "0", is_active: true,
      }, ...prev];
    });
  };

  const addAtEnd = () => {
    setBands((prev) => {
      const last = prev[prev.length - 1];
      const start = last
        ? (last.unbounded
            ? Number(String(last.min).replace(/[^\d.]/g, "") || 0) + 1000
            : Number(String(last.max).replace(/[^\d.]/g, "") || 0))
        : 0;
      const capped = prev.map((b, i) =>
        i === prev.length - 1 && b.unbounded
          ? { ...b, unbounded: false, max: String(start) }
          : b);
      return [...capped, {
        id: undefined, min: String(start), max: "", unbounded: true,
        markup_percent: "0", fee_amount_regular: "0", fee_amount_priority: "0", is_active: true,
      }];
    });
  };

  // Snap every band's start to the previous band's end, closing gaps and
  // overlaps in one go without touching the rates.
  const makeContiguous = () => {
    setBands((prev) => {
      const ordered = [...prev].sort((a, b) =>
        Number(String(a.min).replace(/[^\d.]/g, "") || 0) - Number(String(b.min).replace(/[^\d.]/g, "") || 0));
      let cursor = 0;
      return ordered.map((b, i) => {
        const isLast = i === ordered.length - 1;
        const width = b.unbounded
          ? 0
          : Math.max(0, Number(String(b.max).replace(/[^\d.]/g, "") || 0) - Number(String(b.min).replace(/[^\d.]/g, "") || 0));
        const min = cursor;
        const max = isLast ? "" : String(cursor + width);
        cursor = cursor + width;
        return { ...b, min: String(min), max, unbounded: isLast ? true : false };
      });
    });
  };

  const issues = analyseBands(bands.map((b) => ({
    ...b,
    min: Number(String(b.min).replace(/[^\d.]/g, "") || 0),
    max: Number(String(b.max).replace(/[^\d.]/g, "") || 0),
  })));
  const blocking = issues.filter((i) => i.level === 'error');

  const handleSubmit = async () => {
    if (blocking.length > 0) {
      toast.error("Fix the highlighted problems before saving");
      return;
    }
    const payload = {
      ref_service_id: lane.serviceId,
      fee_name: lane.feeName,
      bands: bands.map((b) => ({
        id: b.id,
        min_trx_amount: toMinor(b.min),
        max_trx_amount: b.unbounded ? UNBOUNDED : toMinor(b.max),
        markup_percent: Number(String(b.markup_percent).replace(/[^\d.]/g, "") || 0),
        fee_currency: currency,
        fee_amount_regular: toMinor(b.fee_amount_regular),
        fee_amount_priority: toMinor(b.fee_amount_priority),
        fee_min: 0,
        fee_max: 0,
        is_active: b.is_active,
      })),
    };
    const action = lane.sourceKey === 'usd'
      ? SaveInternationalUSDtoUSDServiceFeeLadderBIZcore
      : SaveInternationalServiceFeeLadderBIZcore;

    const ok = await dispatch(action(tenantId, payload, setLoading));
    if (ok) {
      await handleGet();
      handleClose();
    }
  };

  if (!lane) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box maxWidth={"lg"} width={"92vw"} className="modal">
          <Box sx={{ padding: "16px 16px 8px 16px" }}>
            <Box className='flexCenter_Row' gap={"10px"} flexWrap={"wrap"}>
              <Typography variant='h6'>{lane.feeName}</Typography>
              <Chip size='small' variant='outlined' label={lane.sourceLabel} color="warning" />
              <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                service id {lane.serviceId} · amounts in {currency}
              </Typography>
            </Box>
            <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block", mt: "4px" }}>
              Bands are min-inclusive and max-exclusive. Split a band to insert one
              in the middle; the whole ladder is saved in a single request.
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>

          <Box sx={{ padding: "12px 16px", maxHeight: "56vh", overflowY: "auto" }}>
            {/* ---------- column headings ---------- */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 90px 1fr 1fr 70px 96px",
                gap: "10px",
                px: "10px",
                pb: "6px",
              }}>
              {["From", "To", "Markup %", "Regular fee", "Priority fee", "Active", ""].map((h, i) => (
                <Typography key={i} variant='caption' sx={{ color: LABEL_COLOR, fontWeight: 600 }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {
              bands.length === 0
              ?
              <Typography variant='body2' sx={{ color: LABEL_COLOR, p: "16px" }}>
                This ladder has no bands. Add one to get started.
              </Typography>
              :
              bands.map((band, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 90px 1fr 1fr 70px 96px",
                    gap: "10px",
                    alignItems: "center",
                    border: "1px solid rgba(44, 45, 47, 1)",
                    background: "rgba(44, 45, 47, 0.44)",
                    borderRadius: "8px",
                    padding: "8px 10px",
                    mb: "6px",
                    opacity: band.is_active ? 1 : 0.55,
                  }}>
                  <TextField size='small' value={band.min}
                    onChange={(e) => patch(index, 'min', e.target.value)} />
                  <TextField size='small'
                    value={band.unbounded ? "∞" : band.max}
                    disabled={band.unbounded}
                    onChange={(e) => patch(index, 'max', e.target.value)} />
                  <TextField size='small' value={band.markup_percent}
                    onChange={(e) => patch(index, 'markup_percent', e.target.value)} />
                  <TextField size='small' value={band.fee_amount_regular}
                    onChange={(e) => patch(index, 'fee_amount_regular', e.target.value)} />
                  <TextField size='small' value={band.fee_amount_priority}
                    onChange={(e) => patch(index, 'fee_amount_priority', e.target.value)} />
                  <Tooltip title={band.is_active ? "Active" : "Inactive"}>
                    <Checkbox checked={band.is_active}
                      onChange={(e) => patch(index, 'is_active', e.target.checked)} />
                  </Tooltip>
                  <Box className='flexCenter_Row'>
                    <Tooltip title="Split this band in two">
                      <IconButton size='small' color='warning' onClick={() => splitBand(index)}>
                        <CallSplitIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove this band">
                      <IconButton size='small' color='error' onClick={() => removeBand(index)}>
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            }

            <Box className='flexCenter_Row' gap={"10px"} mt={"10px"} flexWrap={"wrap"}>
              <Button size='small' startIcon={<AddIcon />} onClick={addAtStart}>
                Add band at start
              </Button>
              <Button size='small' startIcon={<AddIcon />} onClick={addAtEnd}>
                Add band at end
              </Button>
              <Button size='small' startIcon={<AutoFixHighIcon />} onClick={makeContiguous}>
                Make contiguous
              </Button>
            </Box>

            {/* ---------- live validation ---------- */}
            <Box mt={"12px"}>
              {
                issues.length === 0
                ?
                <Box className='flexCenter_Row' gap={"8px"}
                  sx={{ border: `1px solid ${TILE_ACCENT.good}40`, background: `${TILE_ACCENT.good}14`,
                        borderRadius: "8px", padding: "8px 12px" }}>
                  <CheckCircleOutlineOutlinedIcon sx={{ fontSize: "18px", color: TILE_ACCENT.good }} />
                  <Typography variant='caption'>Ladder is contiguous and fully covered.</Typography>
                </Box>
                :
                issues.map((issue, i) => (
                  <Box key={i} className='flexCenter_Row' gap={"8px"} mb={"4px"}
                    sx={{
                      border: `1px dashed ${(issue.level === 'error' ? TILE_ACCENT.bad : TILE_ACCENT.warn)}80`,
                      background: `${(issue.level === 'error' ? TILE_ACCENT.bad : TILE_ACCENT.warn)}14`,
                      borderRadius: "8px", padding: "6px 12px",
                    }}>
                    <ReportProblemOutlinedIcon
                      sx={{ fontSize: "16px", color: issue.level === 'error' ? TILE_ACCENT.bad : TILE_ACCENT.warn }} />
                    <Typography variant='caption'>
                      <b>{issue.level === 'error' ? "Must fix" : "Warning"}</b> · {issue.text}
                    </Typography>
                  </Box>
                ))
              }
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }}/>
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Typography variant='caption' sx={{ color: LABEL_COLOR, marginRight: "auto" }}>
              {bands.length} band{bands.length === 1 ? "" : "s"}
              {blocking.length > 0 && " · fix the errors above to save"}
            </Typography>
            <Button variant='text' onClick={handleClose}>Cancel</Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={blocking.length > 0 || bands.length === 0}
              onClick={handleSubmit}>
              Save ladder
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default LadderEditorModal;
